// drag-session — глобальное состояние «Хватай-и-в-коллекцию».
//
// Источник истины для drag-and-drop карточек в «Док действий». Не персистится
// (как side-panel) — кроме `lastUsedSetIds`, который синхронизируется с
// localStorage, чтобы недавно-использованные коллекции всплывали первыми в
// чипах дока между сессиями.
//
// `pendingResolve` + `openArtifactResolve` (батч 2) питают модал «материал vs
// копия»: bound-артефакт (note/test/flashcard_set/audio_recap/visual_analysis)
// при drop переводит сессию в resolve-state с binding-info материала (id +
// title), а смонтированный в layout `AddArtifactToCollectionModal` читает
// `pendingResolve` и предлагает «весь материал» vs «независимая копия».

import type { SetItemType } from "@studyhub/shared/contracts/materials";
import { create } from "zustand";

export interface DragItem {
	type: SetItemType;
	id: string;
	title: string;
}

/** Сколько недавних коллекций помним (топ дока). */
const LAST_USED_LIMIT = 5;
const LAST_USED_STORAGE_KEY = "studyhub:collections:last-used-sets";

function readLastUsed(): string[] {
	if (typeof window === "undefined") return [];
	try {
		const raw = window.localStorage.getItem(LAST_USED_STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed.filter((v): v is string => typeof v === "string").slice(0, LAST_USED_LIMIT);
	} catch {
		return [];
	}
}

function writeLastUsed(ids: string[]): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(LAST_USED_STORAGE_KEY, JSON.stringify(ids));
	} catch {
		// localStorage недоступен (private mode / quota) — игнорируем, in-memory хватает.
	}
}

interface DragSessionState {
	/** Текущий перетаскиваемый элемент (null = drag не идёт). */
	item: DragItem | null;
	/** Идёт ли drag прямо сейчас (Док действий виден при true). */
	isDragging: boolean;
	start: (item: DragItem) => void;
	end: () => void;

	// ── Резолв «материал vs копия» для bound-артефактов (батч 2) ──
	/**
	 * Элемент + целевая коллекция + binding-info материала, ожидающие решения
	 * пользователя в модале. `materialId`/`materialTitle` приходят из
	 * `sets.getItemBindingInfo` (резолвится в use-collection-drop до открытия).
	 */
	pendingResolve: {
		item: DragItem;
		setId: string;
		materialId: string;
		materialTitle: string;
	} | null;
	openArtifactResolve: (
		item: DragItem,
		setId: string,
		binding: { materialId: string; materialTitle: string },
	) => void;
	closeArtifactResolve: () => void;

	// ── Пикер «Выбрать коллекцию» (масштабирование на десятки коллекций) ──
	/**
	 * Элемент, ожидающий выбора коллекции через searchable-диалог. Ставится при
	 * бросании карточки на цель «Выбрать коллекцию…» в доке — смонтированный в
	 * layout CollectionPickerDialog читает его и показывает поиск по всем коллекциям.
	 */
	pendingPick: DragItem | null;
	openPick: (item: DragItem) => void;
	closePick: () => void;

	// ── Недавно-использованные коллекции (порядок чипов дока) ──
	lastUsedSetIds: string[];
	pushLastUsed: (setId: string) => void;
}

export const useDragSessionStore = create<DragSessionState>((set, get) => ({
	item: null,
	isDragging: false,
	start: (item) => set({ item, isDragging: true }),
	// Конец drag НЕ сбрасывает pendingResolve — модал след. батча живёт после drop.
	end: () => set({ item: null, isDragging: false }),

	pendingResolve: null,
	openArtifactResolve: (item, setId, binding) =>
		set({
			pendingResolve: {
				item,
				setId,
				materialId: binding.materialId,
				materialTitle: binding.materialTitle,
			},
		}),
	closeArtifactResolve: () => set({ pendingResolve: null }),

	pendingPick: null,
	openPick: (item) => set({ pendingPick: item }),
	closePick: () => set({ pendingPick: null }),

	lastUsedSetIds: readLastUsed(),
	pushLastUsed: (setId) => {
		const next = [setId, ...get().lastUsedSetIds.filter((id) => id !== setId)].slice(
			0,
			LAST_USED_LIMIT,
		);
		writeLastUsed(next);
		set({ lastUsedSetIds: next });
	},
}));
